import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services';
import { LicenseTypeEnum, UserDutyEnum } from 'src/enumerators';
import { environment } from 'src/environments/environment';
import { MemberRemovalRequestAttributes } from 'src/models';
import { AppVersion, GlobalNotification } from 'src/models/master';
import { OrganizationWarrantySettingData } from 'src/models/settings';
import { Organization } from '../models';
import { ServiceFunction } from '../utils/servicesStaticFunction';
import { DateService } from './date.service';
import { RestApiService } from './rest-api.service';
import { SettingsService } from './settings.service';
import { SocketService } from './socket.service';
@Injectable({
  providedIn: 'root',
})
export class OrganizationService extends RestApiService {
  public organization: Organization;

  environmentEndpoint = environment.endpoint();
  uri = environment.uri();

  _urlOrganizations = '/organizations';
  _urlOrganizationUsers = '/organization';
  _urlOrganization = '/organizations/:orgId';
  _urlOwnerInvitation = '/owner-invitations';
  _urlOrganizationInvites = '/invites';
  _urlRemoveUser = '/user/:id';
  _urlRemoveMember = '/member/:id';
  _urlChangeUserDuty = '/user/change-duty';
  _urlGetWorkspace = '/workspace';
  _urlGetWorkspaces = '/workspaces';
  _urlSwitchWorkspace = '/switch-workspace';
  _urlGetOrganizationsUser = '/user/organizations';
  _urlChangeLicense = '/master/organizations/:id/license';
  _urlMembers = '/workspace-members';
  _urlGroups = '/groups';
  _urlGroupsId = '/groups/:id';
  _urlGroupsMembers = '/groups/:id/members';
  _urlMemberRemovalRequest = '/member-removal-requests/';
  _urlChangeOwner = '/user/change-owner';
  _urlAppVerion = '/app-version';
  _urlMasterNotification = '/notifications';
  _urlOrganizationNotification = '/notifications/organization';
  _urlMembersActive = '/master/members/active';

  _urlUploadImage = '/organizations/:id/image';

  currentWorkspace: any;
  workSub: BehaviorSubject<any | undefined> = new BehaviorSubject<any | undefined>(undefined);

  listWorkspaces: any[] = [];
  newOrganizationId: any;
  switchOrg: boolean = false;
  modalEmail: string | undefined;
  switchOrgSub: BehaviorSubject<Array<any> | undefined> = new BehaviorSubject<Array<any> | undefined>(undefined);

  constructor(
    private _http: HttpClient,
    private _auth: AuthService,
    private _socket: SocketService,
    private _settingsService: SettingsService,
    private _dateService: DateService,
  ) {
    super(_http);
  }

  // Headers atualizados
  private get authJsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this._auth.getJwtToken()}`,
    });
  }

  private get authOnlyHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this._auth.getJwtToken()}`,
    });
  }

  // =========================
  // Organization
  // =========================

  createOrg(orgForm: any) {
    const body = {
      name: orgForm?.value.name,
      cnpj: orgForm?.value.cnpj,
      licenseType: orgForm?.value.licenseType,
    };
    const url = this.environmentEndpoint + this._urlOrganizations;

    return this._http
      .post<any>(url, body, { headers: this.authJsonHeaders })
      .pipe(
        switchMap((organization) =>
          this.inviteOwner(organization.id, orgForm.value).pipe(
            map(() => ({ organization, ownerInvitationSent: true })),
            catchError((ownerInvitationError) => of({ organization, ownerInvitationSent: false, ownerInvitationError })),
          ),
        ),
      );
  }

  inviteOwner(orgId: string, orgForm: any) {
    const body = {
      firstName: orgForm.firstName,
      lastName: orgForm.lastName,
      email: orgForm.email,
      originUrl: this.uri,
    };
    const url = this.environmentEndpoint + this._urlOrganization.replace(':orgId', orgId) + this._urlOwnerInvitation;
    return this._http.post<any>(url, body, { headers: this.authJsonHeaders });
  }

  setWorkspace() {
    this.getWorkspace().subscribe((res: any) => {
      this.currentWorkspace = res;
      this.currentWorkspace = this.listWorkspaces.find((workspace) => workspace.id == this.currentWorkspace.id);
      this.workSub.next(this.currentWorkspace);
      this.refreshEndOfWarrantyDays();
    });
  }

  // Sincroniza o endOfWarrantyDays (OrganizationWarrantySetting) com
  // ServiceFunction (usado pelo sign.active) e DateService (usado por
  // warranty-view/table); sem isso o front classifica "fim de garantia" com
  // janela hardcoded de 30 dias enquanto o backend usa o valor configurado.
  private refreshEndOfWarrantyDays() {
    this._settingsService.getOrganizationWarrantySetting().subscribe({
      next: (res: OrganizationWarrantySettingData | OrganizationWarrantySettingData[]) => {
        const setting = Array.isArray(res) ? res[0] : res;
        const days = Number(setting?.endOfWarrantyDays);
        if (Number.isFinite(days) && days > 0) {
          ServiceFunction.setEndOfWarrantyDays(days);
          this._dateService.setEndOfWarrantyDays(days);
        }
      },
      error: () => { /* mantém default 30 */ },
    });
  }

  // Método de Reenvio de e-mail
  sendEmailAgain(orgId) {
    return this.getOwner(orgId).pipe(
      switchMap((res) => this.inviteOwner(orgId, res?.[0])),
    );
  }

  getOwner(orgId: string) {
    const url = this.environmentEndpoint + this._urlOrganization.replace(':orgId', orgId) + this._urlOwnerInvitation;
    return this._http.get<any>(url, { headers: this.authJsonHeaders });
  }

  getOrgs() {
    const url = this.environmentEndpoint + this._urlOrganizations;
    return this._http.get<Organization[]>(url, { headers: this.authJsonHeaders });
  }

  getEmail(orgId: string) {
    const url = this.environmentEndpoint + this._urlOrganization.replace(':orgId', orgId) + this._urlOwnerInvitation;
    return this._http.get<any>(url, { headers: this.authJsonHeaders });
  }

  getCurrentOrg(files: boolean | string) {
    const headers = this.authJsonHeaders;
    let url = this.environmentEndpoint + this._urlOrganizationUsers + '?';
    url += `files=${files}&`;

    return this._http.get(url, { headers }).pipe(
      tap((org: Organization) => {
        this.organization = org;
      }),
    );
  }

  editOrganization(orgId: string, orgForm: any) {
    const url = this.environmentEndpoint + this._urlOrganization.replace(':orgId', orgId);
    return this._http.put(url, orgForm, { headers: this.authJsonHeaders });
  }

  updateSignalingPreferences(orgId: string, signalingsFromTaskToPending: boolean) {
    const url = `${this.environmentEndpoint}${this._urlOrganization.replace(':orgId', orgId)}/signaling-preferences`;
    return this._http.patch(url, { signalingsFromTaskToPending }, { headers: this.authJsonHeaders });
  }

  updateDuplicateDetectionSettings(orgId: string, duplicateRadiusMeters: number, duplicateDetectionEnabled: boolean) {
    const url = `${this.environmentEndpoint}${this._urlOrganization.replace(':orgId', orgId)}/duplicate-detection-settings`;
    return this._http.patch(url, { duplicateRadiusMeters, duplicateDetectionEnabled }, { headers: this.authJsonHeaders });
  }

  inviteOrgMember(form: any) {
    const url = this.environmentEndpoint + this._urlOrganizationUsers + this._urlOrganizationInvites;
    return this._http.post(
      url,
      {
        firstName: form.firstName,
        lastName: form.lastName,
        duty: UserDutyEnum.Admin,
        profileIds: form.profileIds,
        email: form.email,
        originUrl: this.uri,
      },
      { headers: this.authJsonHeaders },
    );
  }

  removeInviteMember(emailEmployee: string) {
    const options = {
      headers: this.authJsonHeaders,
      body: { email: emailEmployee },
    };
    const url = this.environmentEndpoint + this._urlOrganizationUsers + this._urlOrganizationInvites;
    return this._http.delete(url, options);
  }

  removeOrgMember(id: string) {
    const url = this.environmentEndpoint + this._urlRemoveMember.replace(':id', id);
    return this._http.delete(url, { headers: this.authJsonHeaders });
  }

  changeMemberDuty(id: string, duty: string) {
    const url = this.environmentEndpoint + this._urlChangeUserDuty;
    return this._http.put(
      url,
      { userId: id, newDuty: duty },
      { headers: this.authJsonHeaders },
    );
  }

  // =========================
  // Workspace
  // =========================

  switchWorkspace(workspaceId: string) {
    const url = this.environmentEndpoint + this._urlSwitchWorkspace + `?workspaceId=${workspaceId}`;
    return this._http.post(url, {}, { headers: this.authJsonHeaders });
  }

  setSwitchWorkspace(workspaceOrId: any) {
    const workspaceId = typeof workspaceOrId === 'string' ? workspaceOrId : (workspaceOrId?.id as string);

    // O centro/zoom salvos pertencem à org anterior; limpa para que o mapa
    // da nova org abra centralizado nas suas próprias sinalizações.
    try { localStorage.removeItem('mapLocations'); } catch { }

    this.switchWorkspace(workspaceId).subscribe({
      next: (session: any) => {
        // aplica sessão + recarrega RBAC com o workspace esperado
        this._auth.applySession(session, { redirectTo: 'home', expectedWorkspaceId: workspaceId });

        this.switchOrg = true;
        this.setWorkspace();

        this._socket.StopNotificationsSocket();
        this._socket.StartNotificationsSocket();
      },
      error: (err) => console.log('switchWorkspace error:', err?.error ?? err),
    });
  }

  getWorkspace() {
    const url = this.environmentEndpoint + this._urlGetWorkspace;
    return this._http.get(url, { headers: this.authJsonHeaders });
  }

  getWorkspaces() {
    const url = this.environmentEndpoint + this._urlGetWorkspaces;
    return this._http.get(url).pipe(
      tap((workspaces: any) => {
        if (Array.isArray(workspaces)) {
          workspaces.forEach((w: any) => {
            if (w?.imageURL) {
              const img = new Image();
              img.decoding = 'async';
              img.src = w.imageURL;
            }
          });
        }
      }),
    );
  }

  getOrganizationById(orgId: string) {
    const url = this.environmentEndpoint + this._urlOrganization.replace(':orgId', orgId);
    return this._http.get(url, { headers: this.authJsonHeaders });
  }

  editOrganizationLicense(orgId: string, license: LicenseTypeEnum) {
    const url = this.environmentEndpoint + this._urlChangeLicense.replace(':id', orgId);
    return this._http.put(url, { licenseType: license }, { headers: this.authJsonHeaders });
  }

  uploadImages(file: File, orgId: string) {
    const reqHeaders = new HttpHeaders({
      Authorization: `Bearer ${this._auth.getJwtToken()}`,
      FormData: 'true',
    });

    const formData = new FormData();
    formData.append('file', file, file.name);

    const url = this.environmentEndpoint + this._urlUploadImage.replace(':id', orgId);
    return this._http.post(url, formData, { headers: reqHeaders });
  }

  // =========================
  // Members / Groups
  // =========================

  listAllWorkspaceUsers(
    filters: { userDuty?: UserDutyEnum; registeredSignaling?: boolean; files?: boolean } = { files: true },
  ) {
    const { userDuty, registeredSignaling, files = true } = filters;

    let params = new HttpParams();
    if (userDuty) params = params.set('userDuty', userDuty);
    if (registeredSignaling != null) params = params.set('registeredSignaling', String(registeredSignaling));
    params = params.set('files', String(files));

    return this._http.get(`${this.environmentEndpoint}${this._urlMembers}`, {
      params,
      headers: this.authJsonHeaders,
    });
  }

  getGroups() {
    const url = this.environmentEndpoint + this._urlGroups;
    return this._http.get(url, { headers: this.authJsonHeaders });
  }

  getGroupId(groupId: string) {
    const url = this.environmentEndpoint + this._urlGroupsId.replace(':id', groupId);
    return this._http.get(url, { headers: this.authJsonHeaders });
  }

  postGroup(group: any) {
    const url = this.environmentEndpoint + this._urlGroups;
    return this._http.post(url, group, { headers: this.authJsonHeaders });
  }

  putGroup(group: any, groupId: string) {
    const url = this.environmentEndpoint + this._urlGroupsId.replace(':id', groupId);
    return this._http.put(url, group, { headers: this.authJsonHeaders });
  }

  deleteGroup(groupId: string) {
    const url = this.environmentEndpoint + this._urlGroupsId.replace(':id', groupId);
    return this._http.delete(url, { headers: this.authJsonHeaders });
  }

  getGroupMembers(groupId: string) {
    const url = this.environmentEndpoint + this._urlGroupsMembers.replace(':id', groupId);
    return this._http.get(url, { headers: this.authJsonHeaders });
  }

  putGroupMember(memberIdList: string[], groupId: string) {
    const url = this.environmentEndpoint + this._urlGroupsMembers.replace(':id', groupId);
    return this._http.put(url, { ids: memberIdList }, { headers: this.authJsonHeaders });
  }

  // =========================
  // Feature flags
  // =========================

  isMeasurementReportEnabled() {
    if (
      this.currentWorkspace?.organizationId == '3e1fdee0-7f75-11ec-960d-bb505088e60d' ||
      this.environmentEndpoint.includes('hellius.dev.')
    ) {
      return true;
    }
    return false;
  }

  isMeasurementReportDetranBAEnabled() {
    if (
      this.currentWorkspace?.organizationId == '1e835990-dd79-11ee-8aeb-5df8769b0208' ||
      this.environmentEndpoint.includes('hellius.dev.')
    ) {
      return true;
    }
    return false;
  }

  isMeasurementReportJoaoPessoaEnabled() {
    if (
      this.currentWorkspace?.organizationId == 'b25f6580-b232-11ef-9c9f-d9fa0be7018e' ||
      this.environmentEndpoint.includes('hellius.dev.')
    ) {
      return true;
    }
    return false;
  }

  // Feature-flag PRÓPRIA do cliente DER-ES: liga o fluxo ASSÍNCRONO (fila) do Relatório
  // de Medição do DER-ES. Desligada (padrão em produção), a tela mantém o comportamento
  // legado síncrono. Habilitada em dev e no ambiente local para rollout controlado; para promover em
  // produção, adicionar o organizationId do DER-ES à condição abaixo.
  isMeasurementReportDERESAsyncEnabled() {
    if (
      this.environmentEndpoint.includes('hellius.dev.') ||
      this.environmentEndpoint.includes('localhost')
    ) {
      return true;
    }
    return false;
  }

  // Feature-flag PRÓPRIA do cliente DETRAN-BA: liga o fluxo ASSÍNCRONO (fila) do Relatório
  // de Medição do DETRAN-BA. Desligada (padrão em produção), a tela mantém o comportamento
  // legado síncrono (atomicidade). Habilitada em dev e no ambiente local para rollout controlado; para
  // promover em produção, adicionar o organizationId do DETRAN-BA à condição abaixo.
  // Decoupled da flag de habilitação do relatório (isMeasurementReportDetranBAEnabled).
  isMeasurementReportDetranBAAsyncEnabled() {
    if (
      this.environmentEndpoint.includes('hellius.dev.') ||
      this.environmentEndpoint.includes('localhost')
    ) {
      return true;
    }
    return false;
  }

  // Feature-flag PRÓPRIA do cliente João Pessoa: liga o fluxo ASSÍNCRONO (fila) do Relatório
  // de Medição do João Pessoa. Desligada (padrão em produção), a tela mantém o comportamento
  // legado síncrono (atomicidade). Habilitada em dev e no ambiente local para rollout controlado; para
  // promover em produção, adicionar o organizationId do João Pessoa à condição abaixo.
  // Decoupled da flag de habilitação do relatório (isMeasurementReportJoaoPessoaEnabled).
  isMeasurementReportJoaoPessoaAsyncEnabled() {
    if (
      this.environmentEndpoint.includes('hellius.dev.') ||
      this.environmentEndpoint.includes('localhost')
    ) {
      return true;
    }
    return false;
  }

  // =========================
  // Removal requests (RestApiService)
  // =========================

  getRemovalMembers() {
    const url = this._urlMemberRemovalRequest;
    return this.get(url, MemberRemovalRequestAttributes);
  }

  postRemovalMembers(removalMember: any) {
    const url = this._urlMemberRemovalRequest;
    return this.post(url, removalMember, MemberRemovalRequestAttributes);
  }

  deleteRemovalMembers(idRemovalMember: string) {
    const url = this._urlMemberRemovalRequest + idRemovalMember;
    return this.del(url, MemberRemovalRequestAttributes);
  }

  // =========================
  // Owner / Version / Notifications
  // =========================

  changeOwnerOrganization(userId: string, password: string) {
    const url = this.environmentEndpoint + this._urlChangeOwner;
    return this._http.put(url, { userId, password }, { headers: this.authJsonHeaders });
  }

  getAppVersion() {
    const url = this._urlAppVerion;
    return this.get(url, AppVersion);
  }

  putAppVersion(body: AppVersion) {
    const url = this._urlAppVerion;
    return this.put(url, body, AppVersion);
  }

  postMasterNotification(body: GlobalNotification) {
    const url = this._urlMasterNotification;
    return this.post(url, body, GlobalNotification);
  }

  postOrganizationNotification(body: GlobalNotification) {
    const url = this._urlOrganizationNotification;
    return this.post(url, body, GlobalNotification);
  }

  putMembersActive(membersIds: string[], active: boolean) {
    const url = this.environmentEndpoint + this._urlMembersActive;
    return this._http.put(url, { membersIds, active });
  }

  // Master: perfis RBAC de uma organização (cross-org)
  getOrgProfiles(orgId: string) {
    const url = `${this.environmentEndpoint}/master/organizations/${orgId}/profiles`;
    return this._http.get<any[]>(url);
  }

  getOrgUserProfiles(orgId: string, userId: string) {
    const url = `${this.environmentEndpoint}/master/organizations/${orgId}/users/${userId}/profiles`;
    return this._http.get<{ profileIds: string[] }>(url);
  }

  updateOrgUserProfiles(orgId: string, userId: string, profileIds: string[]) {
    const url = `${this.environmentEndpoint}/master/organizations/${orgId}/users/${userId}/profiles`;
    return this._http.put(url, { profileIds });
  }
}
